// frontend/src/RedeemCode.js
// ============================================================
// Partnership code redemption page — branded to match site
// ============================================================

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';

const T = {
  navy:      '#10314F',
  navyLight: '#33475C',
  navyMuted: '#5A6B7D',
  gold:      '#EF9F27',
  goldLight: '#FCEBD0',
  bg:        '#F7F5F0',
  border:    '#E6E1D6',
  white:     '#FFFFFF',
  serif:     "Fraunces, Georgia, serif",
  sans:      "Inter, 'Helvetica Neue', Helvetica, Arial, sans-serif",
};

const API_URL = "https://scholarsync-platform-production.up.railway.app";

const LogoSVG = () => (
  <svg width="44" height="44" viewBox="0 0 100 100" aria-hidden="true">
    <rect x="0" y="0" width="100" height="100" rx="24" fill={T.navy} />
    <path d="M50 18 C36 18 26 29 26 42 C26 58 50 84 50 84 C50 84 74 58 74 42 C74 29 64 18 50 18 Z" fill={T.white} />
    <circle cx="50" cy="41" r="8.5" fill={T.gold} />
  </svg>
);

export default function RedeemCode() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [code, setCode] = useState('');
  const [codeValid, setCodeValid] = useState(false);
  const [codeLabel, setCodeLabel] = useState('');
  const [codeError, setCodeError] = useState('');
  const [validating, setValidating] = useState(false);

  const [fullName, setFullName] = useState('');
  const [studentName, setStudentName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  async function validateCode(e) {
    e.preventDefault();
    setCodeError('');
    setValidating(true);
    try {
      const res = await fetch(`${API_URL}/api/partnerships/validate-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim() })
      });
      const data = await res.json();
      if (data.valid) {
        setCodeValid(true);
        setCodeLabel(data.label || '');
      } else {
        setCodeError(data.error || 'Invalid code.');
      }
    } catch {
      setCodeError('Something went wrong. Please try again.');
    }
    setValidating(false);
  }

  async function redeemCode(e) {
    e.preventDefault();
    setCreateError('');
    if (password !== confirmPassword) {
      setCreateError('Passwords do not match.');
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/partnerships/redeem`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: code.trim(),
          email,
          password,
          full_name: fullName,
          student_name: studentName || fullName
        })
      });
      const data = await res.json();
      if (data.success && data.token) {
        login(data.token, data.user);
        navigate('/dashboard');
      } else {
        setCreateError(data.error || 'Something went wrong.');
      }
    } catch {
      setCreateError('Something went wrong. Please try again.');
    }
    setCreating(false);
  }

  return (
    <div style={{ fontFamily: T.sans, color: T.navy, background: T.bg, minHeight: '100vh' }}>
      {/* Nav */}
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 80px', background: T.white, borderBottom: `1px solid ${T.border}`, position: 'sticky', top: 0, zIndex: 100 }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none' }}>
          <LogoSVG />
          <span style={{ fontFamily: T.serif, fontSize: 22, lineHeight: 1, color: T.navy }}>
            <span style={{ fontStyle: 'italic', fontWeight: 400, color: T.navyMuted }}>Your</span>{' '}
            <span style={{ fontWeight: 500 }}>Scholarship Finder</span>
          </span>
        </Link>
        <nav style={{ display: 'flex', alignItems: 'center', gap: 36, fontSize: 15, fontWeight: 500 }}>
          <Link to="/partnerships" style={{ textDecoration: 'none', color: T.navy }}>Partnerships</Link>
          <Link to="/login" style={{ textDecoration: 'none', color: T.navy }}>Log in</Link>
          <Link to="/" style={{ background: T.navy, color: T.white, padding: '11px 20px', borderRadius: 10, fontWeight: 600, fontSize: 15, textDecoration: 'none', fontFamily: T.sans }}>
            Get started
          </Link>
        </nav>
      </header>

      <div style={{ maxWidth: 520, margin: '0 auto', padding: '60px 20px' }}>
        <h1 style={{ fontFamily: T.serif, fontWeight: 500, fontSize: 28, color: T.navy, textAlign: 'center', marginBottom: 8 }}>
          Redeem Your Scholarship License
        </h1>
        <p style={{ fontSize: 16, color: T.navyLight, textAlign: 'center', marginBottom: 32 }}>
          Enter the redemption code you received to activate your Your Scholarship Finder account.
        </p>

        {/* Step 1: Code Entry */}
        <div style={{ background: T.white, padding: 32, borderRadius: 12, border: `1px solid ${T.border}`, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: codeValid ? '#48BB78' : T.navy, color: T.white, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
              {codeValid ? '✓' : '1'}
            </div>
            <h2 style={{ fontFamily: T.serif, fontSize: 20, fontWeight: 500, color: T.navy, margin: 0 }}>Enter Your Code</h2>
          </div>

          {!codeValid ? (
            <form onSubmit={validateCode}>
              <input
                type="text" value={code}
                onChange={e => { setCode(e.target.value.toUpperCase()); setCodeError(''); }}
                placeholder="e.g. SCHI-7K4M"
                style={{ width: '100%', padding: '14px 16px', fontSize: 24, fontFamily: 'monospace', textAlign: 'center', letterSpacing: 4, border: `2px solid ${T.border}`, borderRadius: 8, outline: 'none', boxSizing: 'border-box', marginBottom: 12 }}
                maxLength={10} autoFocus
              />
              {codeError && <p style={{ color: '#E53E3E', fontSize: 14, marginTop: 8, marginBottom: 0 }}>{codeError}</p>}
              <button type="submit" disabled={!code.trim() || validating} style={{ width: '100%', padding: '14px 24px', fontSize: 16, fontWeight: 600, background: T.navy, color: T.white, border: 'none', borderRadius: 10, cursor: 'pointer', marginTop: 16, fontFamily: T.sans }}>
                {validating ? 'Checking...' : 'Validate Code'}
              </button>
            </form>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 24, fontFamily: 'monospace', fontWeight: 600, color: T.navy, letterSpacing: 2 }}>{code.trim().toUpperCase()}</span>
              <span style={{ background: '#C6F6D5', color: '#276749', padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>✓ Valid</span>
              {codeLabel && <p style={{ color: T.navyMuted, fontSize: 14, marginTop: 8, width: '100%' }}>{codeLabel}</p>}
            </div>
          )}
        </div>

        {/* Step 2: Account Creation */}
        {codeValid && (
          <div style={{ background: T.white, padding: 32, borderRadius: 12, border: `1px solid ${T.border}`, marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: T.navy, color: T.white, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>2</div>
              <h2 style={{ fontFamily: T.serif, fontSize: 20, fontWeight: 500, color: T.navy, margin: 0 }}>Create Your Account</h2>
            </div>
            <form onSubmit={redeemCode}>
              <label style={labelStyle}>Your Full Name</label>
              <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Parent or guardian name" style={inputStyle} required />
              <label style={labelStyle}>Student Name</label>
              <input type="text" value={studentName} onChange={e => setStudentName(e.target.value)} placeholder="Leave blank if same as above" style={inputStyle} />
              <label style={labelStyle}>Email Address</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" style={inputStyle} required />
              <label style={labelStyle}>Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 8 characters" style={inputStyle} minLength={8} required />
              <label style={labelStyle}>Confirm Password</label>
              <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter your password" style={inputStyle} minLength={8} required />
              {createError && <p style={{ color: '#E53E3E', fontSize: 14, marginTop: 8, marginBottom: 0 }}>{createError}</p>}
              <button type="submit" disabled={creating} style={{ width: '100%', padding: '14px 24px', fontSize: 16, fontWeight: 600, background: T.navy, color: T.white, border: 'none', borderRadius: 10, cursor: 'pointer', marginTop: 20, fontFamily: T.sans }}>
                {creating ? 'Activating...' : 'Activate My Account'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#4A5568', marginBottom: 6, marginTop: 18 };
const inputStyle = { width: '100%', padding: '12px 14px', fontSize: 15, border: '1px solid #E2E8F0', borderRadius: 8, outline: 'none', boxSizing: 'border-box', fontFamily: "Inter, 'Helvetica Neue', Helvetica, Arial, sans-serif" };
